import { z } from "zod";
import { CrmLifecycleStageSchema, CrmMarketingConsentSchema } from "./crm-activity.js";
// ─── Review Status ─────────────────────────────────────────
export const ContactReviewStatusSchema = z.enum(["provisional", "reviewed", "dismissed"]);
// ─── Base Fields ───────────────────────────────────────────
export const CONTACT_RESERVED_TAGS = ["archived"];
export const ContactEmailDeliverabilityStatusValues = [
    "unknown",
    "deliverable",
    "bounced",
];
export const ContactEmailDeliverabilityStatusSchema = z.enum(ContactEmailDeliverabilityStatusValues);
const CONTACT_RESERVED_TAG_SET = new Set(CONTACT_RESERVED_TAGS);
const ContactTagSchema = z.string().min(1).max(50);
const ContactEditableTagSchema = ContactTagSchema.refine((tag) => !CONTACT_RESERVED_TAG_SET.has(tag.trim().toLowerCase()), { message: 'Use the archive action instead of the "archived" tag' });
export const ContactSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email().toLowerCase(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    /** Courtesy title — Dr, Lord, The Hon., Sir, etc. Free text. */
    honorific: z.string().max(80).optional(),
    /**
     * @deprecated Prefer phoneMobile / phoneOffice / phoneHome.
     * Read alias for mobile when present (one-release compat).
     */
    phone: z.string().max(30).optional(),
    phoneOffice: z.string().max(30).optional(),
    phoneMobile: z.string().max(30).optional(),
    phoneHome: z.string().max(30).optional(),
    jobTitle: z.string().max(200).optional(),
    personalEmail: z.string().email().toLowerCase().optional(),
    department: z.string().max(120).optional(),
    city: z.string().max(120).optional(),
    country: z.string().max(120).optional(),
    companyId: z.string().uuid().nullable(),
    tags: z.array(ContactTagSchema),
    customFields: z.record(z.string(), z.unknown()).default({}),
    alternativeEmails: z.array(z.string().email()),
    isDecisionMaker: z.boolean().default(false),
    emailFrequency: z.enum(["all", "weekly", "monthly", "none"]).default("all"),
    ownerEmail: z.string().email().nullable().default(null),
    source: z.string().max(80).nullable().default(null),
    lifecycleStage: CrmLifecycleStageSchema.default("contact"),
    marketingConsent: CrmMarketingConsentSchema.default("unknown"),
    marketingConsentUpdatedAt: z.string().datetime().nullable().default(null),
    emailDeliverabilityStatus: ContactEmailDeliverabilityStatusSchema.default("unknown"),
    lastBouncedAt: z.string().datetime().nullable().default(null),
    lastBounceReason: z.string().max(500).nullable().default(null),
    lastActivityAt: z.string().datetime().nullable().default(null),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    groupIds: z.array(z.string().uuid()).default([]),
    reviewStatus: ContactReviewStatusSchema.default("reviewed"),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ─── Create / Update ───────────────────────────────────────
/**
 * Find-or-create a partner-tenant contact from a staff directory person
 * (programme body roster / L30 contact / action owner). Names default from
 * the email local-part when omitted.
 */
export const EnsureStaffContactSchema = z.object({
    email: z.string().email().toLowerCase(),
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
});
export const ContactCreateSchema = z.object({
    email: z.string().email().toLowerCase(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    honorific: z.string().max(80).optional(),
    phoneOffice: z.string().max(30).optional(),
    phoneMobile: z.string().max(30).optional(),
    phoneHome: z.string().max(30).optional(),
    jobTitle: z.string().max(200).optional(),
    personalEmail: z.string().email().toLowerCase().optional(),
    department: z.string().max(120).optional(),
    city: z.string().max(120).optional(),
    country: z.string().max(120).optional(),
    companyName: z.string().max(200).optional(),
    tags: z.array(ContactEditableTagSchema).default([]),
    customFields: z.record(z.string(), z.unknown()).optional(),
    isDecisionMaker: z.boolean().optional(),
    emailFrequency: z.enum(["all", "weekly", "monthly", "none"]).optional(),
    ownerEmail: z.string().email().nullable().optional(),
    source: z.string().max(80).nullable().optional(),
    lifecycleStage: CrmLifecycleStageSchema.optional(),
    marketingConsent: CrmMarketingConsentSchema.optional(),
    marketingConsentUpdatedAt: z.string().datetime().nullable().optional(),
    emailDeliverabilityStatus: ContactEmailDeliverabilityStatusSchema.optional(),
    lastBouncedAt: z.string().datetime().nullable().optional(),
    lastBounceReason: z.string().max(500).nullable().optional(),
    // reviewStatus is intentionally absent — it is server-set on create;
    // clients may only update it via ContactUpdateSchema.
});
export const ContactUpdateSchema = ContactCreateSchema.partial()
    .omit({ email: true })
    .extend({ reviewStatus: ContactReviewStatusSchema.optional() });
export const ContactBulkUpdateSchema = z.object({
    contactIds: z.array(z.string().uuid()).min(1).max(500),
    addTags: z.array(ContactEditableTagSchema).optional(),
    removeTags: z.array(ContactTagSchema).optional(),
    updates: ContactUpdateSchema.optional(),
});
// ─── Query ─────────────────────────────────────────────────
export const ContactListQuerySchema = z.object({
    search: z.string().max(200).optional(),
    tag: z.string().max(50).optional(),
    companyId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    lifecycleStage: CrmLifecycleStageSchema.optional(),
    // Admin-only opt-in views. Server enforces role; non-admins always see
    // active-only regardless of these flags.
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    sort: z.enum(["name", "email", "createdAt", "updatedAt"]).default("name"),
    order: z.enum(["asc", "desc"]).default("asc"),
});
// ─── Merge ─────────────────────────────────────────────────
export const ContactMergeSchema = z
    .object({
    sourceId: z.string().uuid(),
    targetId: z.string().uuid(),
})
    .refine((data) => data.sourceId !== data.targetId, {
    message: "Source and target contacts must be different",
});
// ─── Duplicate Candidates ─────────────────────────────────
export const ContactDuplicateQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const ContactDuplicateReasonSchema = z.enum(["same_name_company", "same_phone"]);
export const ContactDuplicateCandidateSchema = z.object({
    id: z.string().min(1),
    reason: ContactDuplicateReasonSchema,
    confidence: z.number().min(0).max(1),
    summary: z.string().min(1),
    contacts: z.array(ContactSchema).min(2),
});
export const ContactDuplicateListResponseSchema = z.object({
    candidates: z.array(ContactDuplicateCandidateSchema),
    total: z.number().int(),
});
// ─── Response ──────────────────────────────────────────────
export const ContactListResponseSchema = z.object({
    contacts: z.array(ContactSchema),
    total: z.number().int(),
});
/** Display name with optional honorific — "Dr Jane Smith". */
export function formatContactName(contact) {
    const name = `${contact.firstName} ${contact.lastName}`.trim();
    const honorific = contact.honorific?.trim();
    return honorific ? `${honorific} ${name}` : name;
}
/** Compact phone for list/duplicate cards — mobile, else office, else home. */
export function formatContactPhone(contact) {
    const value = contact.phoneMobile?.trim() ||
        contact.phoneOffice?.trim() ||
        contact.phoneHome?.trim() ||
        contact.phone?.trim() ||
        "";
    return value || undefined;
}
//# sourceMappingURL=contacts.js.map